import { castToBoolean } from '../util';

class Event {
  constructor(data) {
    data                                   = data || {};
    this.additional_event_notes            = data.additional_event_notes || '';
    this.city                              = data.city || null;
    this.claim_types                       = data.claim_types || [];
    this.claims_locked                     = castToBoolean(data.claims_locked);
    this.client_name                       = data.client_name || null;
    this.clients_id                        = data.clients_id || null;
    this.end_timestamp                     = data.end_timestamp || null;
    this.events_recaps_id                  = data.events_recaps_id || null;
    this.events_types_id                   = data.events_types_id || null;
    this.events_types_name                 = data.events_types_name || null;
    this.events_types_name_l10n            = data.events_types_name_l10n || [];
    this.id                                = data.id || null;
    this.lat                               = data.lat || null;
    this.location_variables                = data.location_variables || null;
    this.lon                               = data.lon || null;
    this.markets_id                        = data.markets_id || null;
    this.maconomy_brand                    = data.maconomy_brand;
    this.markets_name                      = data.markets_name || null;
    this.name                              = data.name || null;
    this.opt_out_travel_standard           = data.opt_out_travel_standard;
    this.opt_out_travel_tiered             = data.opt_out_travel_tiered;
    this.pdf_url                           = data.pdf_url || null;
    this.phone                             = data.phone || null;
    this.programs_id                       = data.programs_id || null;
    this.programs_name                     = data.programs_name || null;
    this.recapDefinition                   = data.recapDefinition || null;
    this.recaps_locked                     = castToBoolean(data.recaps_locked);
    this.recap_rejected                    = castToBoolean(data.recap_rejected);
    this.recap_rejected_message            = data.recap_rejected_message || null;
    // Nothing reads recap_rejected_time yet; it is kept for parity with the other two columns the
    // mirror writes, and because EventService already parses it into a Date against the server
    // timezone (datetimeFieldsRelativeToServerTimezone) - before this it did that work and the
    // model then threw the result away.
    this.recap_rejected_time               = data.recap_rejected_time || null;
    this.secondaryClaims                   = data.secondaryClaims || [];
    this.start_timestamp                   = data.start_timestamp || null;
    this.state                             = data.state || null;
    this.street_address1                   = data.street_address1 || null;
    this.street_address2                   = data.street_address2 || null;
    this.venue_name                        = data.venue_name || null;
    this.venue_types_name                  = data.venue_types_name || null;
    this.venues_id                         = data.venues_id || null;
    this.venues_types_id                   = data.venues_types_id || null;
    this.venues_types_name                 = data.venues_types_name || null;
    this.venues_chains_id                  = data.venues_chains_id || null;
    this.venues_chains_name                = data.venues_chains_name || null;
    this.venue_timezone                    = data.venue_timezone || null;
    this.vpid                              = data.vpid || null;
    this.workers                           = data.workers || null;
    this.zip                               = data.zip || null;
    this.programs_start_date               = data.programs_start_date || null;
    this.programs_end_date                 = data.programs_end_date || null;
    this.recap_available_events_start_time = data.recap_available_events_start_time || null;
    this.recap_available_events_end_time   = data.recap_available_events_end_time || null;
    this.prompt_recap_claim                = data.prompt_recap_claim || null;
    this.prompt_recap_checkout_claim       = data.prompt_recap_checkout_claim || null;
    this.events_statuses_definition        = data.events_statuses_definition || null;
    this.events_statuses_id                = data.events_statuses_id || null;
    this.events_statuses_name              = data.events_statuses_name || null;
    this.timezoneName                      = data.timezoneName || null;
    this.venue_custom_fields_by_name       = data.venue_custom_fields_by_name || null;
    this.scheduling_fields                 = data.scheduling_fields || null;
    this.date                              = data.date || null;
    this.start_time                        = data.start_time || null;
    this.end_time                          = data.end_time || null;
    this.expiration_date                   = data.expiration_date || null;

    // Event products (per-event product data entry in recaps)
    this.has_products                      = castToBoolean(data.has_products);
    this.event_products                    = data.event_products || [];
    this.product_issue_types               = data.product_issue_types || [];

    // Merchandising recap scope, embedded by the staff EventSerializer so the
    // recap's per-instance activity-type/product pickers work offline.
    this.merchandising_recap_scope         = data.merchandising_recap_scope || null;

    // Program/brand/product-level documents attached to the event's program,
    // embedded by the staff EventSerializer so the position Files list can show
    // them alongside the position's own documents.
    this.program_documents                 = data.program_documents || [];

    // Postgres can't sort a json object for us in the query so we have to sort here
    if (this.scheduling_fields != null) {
      this.scheduling_fields.sort(function(a,b) {
        return a.display_order < b.display_order ? -1 : 1;
      });
      this.scheduling_fields.forEach(function(el, index) {
        if (el.value == 't') el.value = 'Yes';
        if (el.value == 'f') el.value = 'No';
      });
    }
  }


  get claimTypes() {
    return this.claim_types || [];
  }

  get primaryClaimTypes() {
    return this.claimTypes.filter(claimType =>
      claimType.isTimeBased &&
      (claimType.allow_primary_claim === true || claimType.allow_primary_claim === 't')
    );
  }

  claimTypesForCategoryId(claimCategoryId) {
    return this.claimTypes.filter(claimType => claimType.claim_categories_id == claimCategoryId);
  }

  claimTypesForCategory(claimCategory) {
    return this.claimTypesForCategoryId(claimCategory.id);
  }

  isApprovedRecapped() {
    return this.events_statuses_definition === 'STATUS_APPROVED';
  }

  /**
   * True when the event's own status says the recap is done - either submitted
   * and awaiting approval (Recapped) or approved (Finished). Used by the
   * merchandising dashboard to classify a booking as Complete without relying
   * on the separately-synced recap response, which may be absent client-side.
   */
  isRecapDone() {
    return this.events_statuses_definition === 'STATUS_APPROVED'
      || this.events_statuses_definition === 'STATUS_EVAL_SUBMITTED';
  }

  /**
   * True when the office has rejected this event's recap. Read from the event's own columns
   * rather than from the recap response, which is synced in a separate, window-limited store
   * and is frequently absent client-side.
   */
  isRecapRejected() {
    return this.recap_rejected === true;
  }

  isLastMinuteCancelled() {
    return this.events_statuses_definition === 'STATUS_LM_CANCELLED'
  }

  isCancelled()
  {
    return this.events_statuses_id == 9;
  }
}

export default Event;
